import React, { useState, useEffect, useRef } from "react";
import { Popover, PopoverBody, Input, Button } from "reactstrap";
import { useIntl, FormattedMessage } from "react-intl";

const SupervisorPicker = ({
  target,
  isOpen,
  toggle,
  supervisors,
  programTeachers,
  excludedIds,
  onPick,
  onAddNew,
}) => {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) setQuery("");
  }, [isOpen]);

  const focusSearchInput = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus({ preventScroll: true });
    }
  };

  const handleSelect = (item) => {
    onPick(item);
    toggle();
  };

  const handleAddNew = () => {
    onAddNew();
    toggle();
  };

  const lq = query.toLowerCase();
  const excluded = new Set(excludedIds);

  const visibleCompanySups = supervisors.filter(
    (s) =>
      !excluded.has(s.id) &&
      (!lq ||
        s.name.toLowerCase().includes(lq) ||
        (s.aplCompany?.name || "").toLowerCase().includes(lq)),
  );

  const visibleTeachers = programTeachers
    .filter(
      (t) => !excluded.has(t.id) && (!lq || t.name.toLowerCase().includes(lq)),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const grouped = {};
  visibleCompanySups
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((s) => {
      const co = s.aplCompany?.name || "";
      if (!grouped[co]) grouped[co] = [];
      grouped[co].push(s);
    });

  return (
    <Popover
      placement="bottom-start"
      isOpen={isOpen}
      target={target}
      toggle={toggle}
      onOpened={focusSearchInput}
      hideArrow={false}
      trigger="legacy"
      popperClassName="aplmod-picker"
    >
      <PopoverBody>
        <div className="aplmod-picker__search">
          <Input
            innerRef={searchInputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={intl.formatMessage({
              id: "SearchSupervisorOrTeacher",
            })}
          />
        </div>
        <div className="aplmod-picker__list">
          {Object.keys(grouped)
            .sort((a, b) => a.localeCompare(b))
            .map((company) => (
              <div key={company}>
                <div className="aplmod-picker__group-header aplmod-picker__group-header--sticky">
                  {company}
                </div>
                {grouped[company].map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="aplmod-picker__row"
                    onClick={() => handleSelect(s)}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            ))}

          {visibleTeachers.length > 0 ? (
            <div>
              <div className="aplmod-picker__group-header">
                <FormattedMessage id="ProgramTeachersHeader" />
              </div>
              {visibleTeachers.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="aplmod-picker__row"
                  onClick={() => handleSelect({ ...t, teacher: true })}
                >
                  <span>{t.name}</span>
                  <span className="aplmod-picker__teacher-tag">
                    <FormattedMessage id="TeacherSuffix" />
                  </span>
                </button>
              ))}
            </div>
          ) : (
            ""
          )}

          {visibleCompanySups.length === 0 && visibleTeachers.length === 0 ? (
            <div className="aplmod-picker__empty">
              <FormattedMessage id="NoMatches" />
            </div>
          ) : (
            ""
          )}
        </div>
        <div className="aplmod-picker__footer">
          <Button outline color="primary" block onClick={handleAddNew}>
            + <FormattedMessage id="AddNewSupervisor" />
          </Button>
        </div>
      </PopoverBody>
    </Popover>
  );
};

export default SupervisorPicker;
